// Pure derivation of the calendar grid from the sales-api availability-index
// response. The response is day-major (days → unit groups → rate plans); the
// calendar renders it unit-group-major (a room-type header row of house counts,
// then a rate-plan row per rate plan), one cell per visible night. All of that
// pivot + status logic lives here so the components stay render-only.
// Nightly house sellable count at or below this is treated as low inventory.
export const LOW_INVENTORY_THRESHOLD = 5;
// The day-status the calendar legend colours by, driven by house sellable count.
const houseStatus = (sellable) => {
    if (sellable === null || sellable === undefined) {
        return 'unknown';
    }
    if (sellable <= 0) {
        return 'soldout';
    }
    if (sellable <= LOW_INVENTORY_THRESHOLD) {
        return 'low';
    }
    return 'available';
};
// Human-readable restriction badges for a night. Min-stay of 1 is not a real
// restriction, so it is dropped.
export const restrictionLabels = (restrictions) => {
    if (!restrictions) {
        return [];
    }
    const labels = [];
    const min = restrictions.minLengthOfStay;
    if (min && min > 1) {
        labels.push(`Min ${min} nights`);
    }
    if (restrictions.maxLengthOfStay) {
        labels.push(`Max ${restrictions.maxLengthOfStay} nights`);
    }
    if (restrictions.closed) {
        labels.push('Closed');
    }
    if (restrictions.closedOnArrival) {
        labels.push('No arrival');
    }
    if (restrictions.closedOnDeparture) {
        labels.push('No departure');
    }
    return labels;
};
// The nightly price to display: the entry matching the searched occupancy, else
// the first offered price.
const pickPrice = (prices, adults) => {
    const match = prices.find(p => p.adults === adults) ?? prices[0];
    return {
        price: match?.price?.grossAmount ?? null,
        currency: match?.price?.currency ?? null
    };
};
const emptyAccumulator = (name) => ({
    name,
    houseByDate: new Map(),
    ratePlanOrder: [],
    ratePlanCells: new Map()
});
// Indexes the response by unit group (first-seen order preserved), collecting
// each unit group's per-date house counts and per-rate-plan offers.
const indexByUnitGroup = (result) => {
    const order = [];
    const byId = new Map();
    for (const day of result.days) {
        for (const unitGroup of day.unitGroups) {
            const id = unitGroup.unitGroupId;
            const accumulator = byId.get(id) ?? emptyAccumulator(unitGroup.unitGroupName ?? id);
            if (!byId.has(id)) {
                order.push(id);
                byId.set(id, accumulator);
            }
            accumulator.houseByDate.set(day.date, unitGroup.house?.sellableCount ?? null);
            for (const ratePlan of unitGroup.ratePlans) {
                const cells = accumulator.ratePlanCells.get(ratePlan.ratePlanId) ??
                    new Map();
                if (!accumulator.ratePlanCells.has(ratePlan.ratePlanId)) {
                    accumulator.ratePlanOrder.push(ratePlan.ratePlanId);
                    accumulator.ratePlanCells.set(ratePlan.ratePlanId, cells);
                }
                cells.set(day.date, ratePlan);
            }
        }
    }
    return { order, byId };
};
const buildRatePlanRow = (ratePlanId, cellsByDate, dates, dayStatus, ratePlanNames, adults) => ({
    ratePlanId,
    ratePlanName: ratePlanNames[ratePlanId] ?? ratePlanId,
    cells: dates.map(date => {
        const offer = cellsByDate.get(date);
        if (!offer) {
            return {
                date,
                status: 'unknown',
                price: null,
                currency: null,
                unitsLeft: null,
                restrictions: [],
                hasOffer: false
            };
        }
        const { price, currency } = pickPrice(offer.prices, adults);
        const unitsLeft = offer.available ?? offer.availableUnits ?? null;
        // The day's colour is house-driven; a rate plan with zero units left is its
        // own sold-out cell regardless of the house.
        const status = unitsLeft !== null && unitsLeft <= 0
            ? 'soldout'
            : dayStatus.get(date) ?? 'unknown';
        return {
            date,
            status,
            price,
            currency,
            unitsLeft,
            restrictions: restrictionLabels(offer.restrictions),
            hasOffer: true
        };
    })
});
// A room type is available when at least one visible night has known inventory
// left (sellable > 0). All-sold-out or wholly-unknown rooms are unavailable.
export const isRoomTypeAvailable = (roomType) => roomType.houseCells.some(cell => cell.sellable !== null && cell.sellable > 0);
// Drops sold-out room types from the grid when the salesperson has opted to
// hide unavailable rooms. Pure — mirrors the offer-list filter for the calendar.
export const filterAvailableRoomTypes = (grid, hideUnavailable) => hideUnavailable
    ? { ...grid, roomTypes: grid.roomTypes.filter(isRoomTypeAvailable) }
    : grid;
export const buildAvailabilityGrid = (result, options = {}) => {
    const adults = options.adults ?? 1;
    const ratePlanNames = options.ratePlanNames ?? {};
    const dates = result.days.map(day => day.date);
    const { order, byId } = indexByUnitGroup(result);
    const roomTypes = order.flatMap(id => {
        const accumulator = byId.get(id);
        if (!accumulator) {
            return [];
        }
        const houseCells = dates.map(date => {
            const sellable = accumulator.houseByDate.get(date) ?? null;
            return { date, sellable, status: houseStatus(sellable) };
        });
        const dayStatus = new Map(houseCells.map(cell => [cell.date, cell.status]));
        const ratePlanRows = accumulator.ratePlanOrder.flatMap(ratePlanId => {
            const cells = accumulator.ratePlanCells.get(ratePlanId);
            if (!cells) {
                return [];
            }
            return [
                buildRatePlanRow(ratePlanId, cells, dates, dayStatus, ratePlanNames, adults)
            ];
        });
        return [
            {
                unitGroupId: id,
                unitGroupName: accumulator.name,
                houseCells,
                ratePlanRows
            }
        ];
    });
    return { dates, roomTypes };
};
